import React, { useState, useEffect, useMemo, useCallback } from 'react';
import masterService from '../services/masterService';
import registrationConfigService from '../services/registrationConfigService';
import CommonPagination from '../components/common/CommonPagination';
import usePagination from '../hooks/usePagination';
import './RegistrationConfigPage.css';

export const RegistrationConfigPage = () => {
  const [centres, setCentres] = useState([]);
  const [selectedCentreId, setSelectedCentreId] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [sections, setSections] = useState([]);
  const [autoCollectOnRegistration, setAutoCollectOnRegistration] = useState(false);
  const [autoReceiveOnRegistration, setAutoReceiveOnRegistration] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // 1. Load Centres on Mount
  useEffect(() => {
    const fetchCentres = async () => {
      try {
        const res = await masterService.getCentres({ isActive: true });
        if (res.success && res.data) {
          setCentres(res.data);
          if (res.data.length > 0) {
            setSelectedCentreId(res.data[0].centreId);
          }
        }
      } catch (err) {
        console.error('Error fetching centres:', err);
        showToast('Failed to load centres.', 'error');
      }
    };
    fetchCentres();
  }, []);

  // 2. Load Effective Configuration when Selected Centre changes
  const loadConfiguration = useCallback(async (centreId) => {
    setLoading(true);
    try {
      const res = await registrationConfigService.getEffectiveConfig(centreId);
      if (res.success && res.data) {
        setSections(res.data.sections || []);
        setAutoCollectOnRegistration(!!res.data.autoCollectOnRegistration);
        setAutoReceiveOnRegistration(!!res.data.autoReceiveOnRegistration);
        // Map effective fields into editable state
        const fields = (res.data.fields || []).map((f) => ({
          fieldKey: f.fieldKey,
          sectionKey: f.sectionKey,
          fieldName: f.fieldName,
          displayLabel: f.effectiveLabel,
          visible: f.effectiveVisible,
          enabled: f.effectiveEnabled,
          required: f.effectiveRequired,
          readOnly: f.effectiveReadOnly,
          displayOrder: f.effectiveOrder,
          columnSpan: f.effectiveColumnSpan,
          dataType: f.dataType,
          placeholder: f.placeholder,
          lookupSource: f.lookupSource,
        }));
        setFieldConfigs(fields);
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      showToast('Failed to load registration configuration.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCentreId) {
      loadConfiguration(selectedCentreId);
    }
  }, [selectedCentreId, loadConfiguration]);

  // Handle Field attribute mutations
  const updateField = (fieldKey, key, value) => {
    setHasChanges(true);
    setFieldConfigs((prev) =>
      prev.map((f) => {
        if (f.fieldKey !== fieldKey) return f;
        const updated = { ...f, [key]: value };

        // Safety Invariant: If field is made invisible, it cannot be required
        if (key === 'visible' && value === false) {
          updated.required = false;
        }

        return updated;
      })
    );
  };

  // Save Configuration
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        centreId: selectedCentreId,
        autoCollectOnRegistration,
        autoReceiveOnRegistration,
        fields: fieldConfigs.map((f) => ({
          fieldKey: f.fieldKey,
          displayLabel: f.displayLabel,
          visible: f.visible,
          enabled: f.enabled,
          required: f.visible ? f.required : false,
          readOnly: f.readOnly,
          displayOrder: parseInt(f.displayOrder, 10) || 1,
          columnSpan: f.columnSpan,
          placeholder: f.placeholder,
        })),
        sections: [],
      };

      const res = await registrationConfigService.saveConfiguration(selectedCentreId, payload);
      if (res.success) {
        showToast('Registration field & workflow configuration saved successfully!');
        setHasChanges(false);
        loadConfiguration(selectedCentreId);
      } else {
        showToast(res.message || 'Failed to save configuration.', 'error');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      showToast('Failed to save configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Reset Configuration to Default
  const handleReset = async () => {
    if (!window.confirm('Reset this centre to platform / lab defaults? All custom overrides will be removed.')) {
      return;
    }
    setSaving(true);
    try {
      const res = await registrationConfigService.resetToDefault(selectedCentreId);
      if (res.success) {
        showToast('Configuration reset to defaults successfully.');
        setHasChanges(false);
        loadConfiguration(selectedCentreId);
      } else {
        showToast(res.message || 'Failed to reset configuration.', 'error');
      }
    } catch (err) {
      console.error('Error resetting config:', err);
      showToast('Failed to reset configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Filtered fields list
  const filteredFields = useMemo(() => {
    return fieldConfigs
      .filter((f) => {
        if (activeTab !== 'all' && f.sectionKey !== activeTab) return false;
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
          f.fieldName.toLowerCase().includes(q) ||
          f.fieldKey.toLowerCase().includes(q) ||
          (f.displayLabel && f.displayLabel.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [fieldConfigs, activeTab, searchQuery]);

  // Centralized Pagination for configuration fields
  const fieldPagination = usePagination({
    items: filteredFields,
    resetDeps: [selectedCentreId, activeTab, searchQuery],
  });

  return (
    <div className="cfg-container">
      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '6px',
            background: toast.type === 'error' ? '#ef4444' : '#10b981',
            color: '#fff',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* 1. Page Header Card */}
      <div className="cfg-header-card">
        <div className="cfg-title-area">
          <div className="cfg-title-badge-row">
            <span className="cfg-module-tag">LIMS CONFIGURATION MASTER</span>
            <span className="cfg-badge-version">National Suite</span>
          </div>
          <h1 className="cfg-page-title">Registration Field Configuration Master</h1>
          <p className="cfg-page-subtitle">
            Configure dynamic field visibility, custom labels, mandatory validation rules, and grid display order per Laboratory & Operating Centre.
          </p>
        </div>
        <div className="cfg-header-actions">
          <button
            type="button"
            className="cfg-btn cfg-btn-secondary"
            onClick={handleReset}
            disabled={saving || loading}
            title="Reset this centre to platform defaults"
          >
            <span className="cfg-btn-icon">↺</span>
            <span>Reset to Default</span>
          </button>
          <button
            type="button"
            className="cfg-btn cfg-btn-preview"
            onClick={() => setPreviewOpen(true)}
            title="Preview how registration form will render for this centre"
          >
            <span className="cfg-btn-icon">👁</span>
            <span>Preview Form</span>
          </button>
          <button
            type="button"
            className="cfg-btn cfg-btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
            title="Commit configuration changes to database"
          >
            <span className="cfg-btn-icon">💾</span>
            <span>{saving ? 'Saving...' : hasChanges ? 'Save Configuration *' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* 2. Centre Selection & Field Search Toolbar */}
      <div className="cfg-toolbar-card">
        <div className="cfg-toolbar-col cfg-toolbar-centre">
          <label htmlFor="centre-select" className="cfg-control-label">
            CONFIGURED CENTRE / BRANCH
          </label>
          <div className="cfg-input-affix-wrapper">
            <span className="cfg-input-affix-icon">🏥</span>
            <select
              id="centre-select"
              className="cfg-select-control"
              value={selectedCentreId}
              onChange={(e) => setSelectedCentreId(parseInt(e.target.value, 10))}
            >
              {centres.map((c) => (
                <option key={c.centreId} value={c.centreId}>
                  {c.centreName} ({c.centreCode})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="cfg-toolbar-col cfg-toolbar-search">
          <label htmlFor="field-search" className="cfg-control-label">
            SEARCH FIELDS & CONTROLS
          </label>
          <div className="cfg-input-affix-wrapper">
            <span className="cfg-input-affix-icon">🔍</span>
            <input
              id="field-search"
              type="text"
              className="cfg-search-control"
              placeholder="Search fields by name or key (e.g. maritalStatus, srfId)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="cfg-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3. Centre Post-Registration Workflow Configuration */}
      <div className="cfg-automation-card">
        <div className="cfg-automation-header">
          <div className="cfg-section-tag">AUTOMATIONS</div>
          <h2 className="cfg-automation-title">Post-Registration Workflow Automation</h2>
          <p className="cfg-automation-subtitle">
            Configure automated sample collection and receiving triggers for registrations processed at this centre.
          </p>
        </div>

        <div className="cfg-automation-grid">
          {/* Option 1: Auto Sample Collection */}
          <label className={`cfg-auto-item ${autoCollectOnRegistration ? 'is-active' : ''}`}>
            <div className="cfg-auto-checkbox-col">
              <input
                type="checkbox"
                className="cfg-checkbox-lg"
                checked={autoCollectOnRegistration}
                onChange={(e) => {
                  const val = e.target.checked;
                  setAutoCollectOnRegistration(val);
                  if (!val) setAutoReceiveOnRegistration(false);
                  setHasChanges(true);
                }}
              />
            </div>
            <div className="cfg-auto-content">
              <div className="cfg-auto-title-row">
                <span className="cfg-auto-title">Enable Auto Sample Collection</span>
                {autoCollectOnRegistration ? (
                  <span className="cfg-badge-status active">Enabled</span>
                ) : (
                  <span className="cfg-badge-status inactive">Off</span>
                )}
              </div>
              <p className="cfg-auto-desc">
                Enables prompt / automatic collection of required specimens directly upon patient registration at this centre.
              </p>
            </div>
          </label>

          {/* Option 2: Auto Sample Receiving */}
          <label
            className={`cfg-auto-item ${autoReceiveOnRegistration ? 'is-active' : ''} ${
              !autoCollectOnRegistration ? 'is-disabled' : ''
            }`}
          >
            <div className="cfg-auto-checkbox-col">
              <input
                type="checkbox"
                className="cfg-checkbox-lg"
                checked={autoReceiveOnRegistration}
                disabled={!autoCollectOnRegistration}
                onChange={(e) => {
                  setAutoReceiveOnRegistration(e.target.checked);
                  setHasChanges(true);
                }}
              />
            </div>
            <div className="cfg-auto-content">
              <div className="cfg-auto-title-row">
                <span className="cfg-auto-title">Enable Auto Sample Receiving</span>
                {autoReceiveOnRegistration ? (
                  <span className="cfg-badge-status active">Enabled</span>
                ) : !autoCollectOnRegistration ? (
                  <span className="cfg-badge-status dependent">Requires Auto Collection</span>
                ) : (
                  <span className="cfg-badge-status inactive">Off</span>
                )}
              </div>
              <p className="cfg-auto-desc">
                Enables immediate transition of collected specimens into received status at the processing laboratory upon registration.
                {!autoCollectOnRegistration && (
                  <span className="cfg-auto-dependency-hint">
                    {' '}(Requires Auto Sample Collection to be enabled first)
                  </span>
                )}
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* 4. Registration Section Navigation Tabs */}
      <div className="cfg-nav-card">
        <div className="cfg-nav-tabs" role="tablist">
          <button
            type="button"
            className={`cfg-nav-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            role="tab"
            aria-selected={activeTab === 'all'}
          >
            <span className="cfg-nav-tab-label">All Sections</span>
            <span className="cfg-nav-tab-count">{fieldConfigs.length}</span>
          </button>
          {sections.map((s) => {
            const count = fieldConfigs.filter((f) => f.sectionKey === s.sectionKey).length;
            return (
              <button
                key={s.sectionKey}
                type="button"
                className={`cfg-nav-tab ${activeTab === s.sectionKey ? 'active' : ''}`}
                onClick={() => setActiveTab(s.sectionKey)}
                role="tab"
                aria-selected={activeTab === s.sectionKey}
              >
                <span className="cfg-nav-tab-label">{s.effectiveLabel || s.sectionName}</span>
                <span className="cfg-nav-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 5. Configuration Table Card */}
      <div className="cfg-table-card">
        {loading ? (
          <div className="cfg-loading-state">
            <span className="cfg-loading-spinner">⚡</span>
            <span>Loading registration field configurations...</span>
          </div>
        ) : (
          <>
            <div className="cfg-table-responsive">
              <table className="cfg-table">
                <thead>
                  <tr>
                    <th style={{ width: '24%' }}>Field Name & Key</th>
                    <th style={{ width: '12%' }}>Section</th>
                    <th style={{ width: '24%' }}>Display Label</th>
                    <th style={{ textAlign: 'center', width: '8%' }}>Visible</th>
                    <th style={{ textAlign: 'center', width: '8%' }}>Required</th>
                    <th style={{ textAlign: 'center', width: '8%' }}>Read Only</th>
                    <th style={{ textAlign: 'center', width: '8%' }}>Enabled</th>
                    <th style={{ textAlign: 'center', width: '8%' }}>Order</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldPagination.totalRecords === 0 ? (
                    <tr>
                      <td colSpan="8" className="cfg-empty-cell">
                        <div className="cfg-empty-state">
                          <span className="cfg-empty-icon">🔍</span>
                          <span className="cfg-empty-title">No matching fields found</span>
                          <span className="cfg-empty-subtitle">Try adjusting your search query or switching section tabs.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    fieldPagination.paginatedItems.map((f) => (
                      <tr key={f.fieldKey} className={`cfg-row ${!f.visible ? 'is-row-hidden' : ''}`}>
                        <td>
                          <span className="cfg-field-name">{f.fieldName}</span>
                          <span className="cfg-field-key">{f.fieldKey}</span>
                        </td>
                        <td>
                          <span className="cfg-badge cfg-badge-section">{f.sectionKey}</span>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="cfg-input-label"
                            value={f.displayLabel || ''}
                            placeholder={f.fieldName}
                            onChange={(e) => updateField(f.fieldKey, 'displayLabel', e.target.value)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            className="cfg-table-checkbox"
                            checked={f.visible}
                            title={f.visible ? 'Field is visible on registration form' : 'Field is hidden'}
                            onChange={(e) => updateField(f.fieldKey, 'visible', e.target.checked)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            className="cfg-table-checkbox"
                            checked={f.required}
                            disabled={!f.visible}
                            title={!f.visible ? 'Hidden fields cannot be mandatory' : f.required ? 'Field is mandatory' : 'Field is optional'}
                            onChange={(e) => updateField(f.fieldKey, 'required', e.target.checked)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            className="cfg-table-checkbox"
                            checked={f.readOnly}
                            title={f.readOnly ? 'Field is read-only for users' : 'Field is editable'}
                            onChange={(e) => updateField(f.fieldKey, 'readOnly', e.target.checked)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            className="cfg-table-checkbox"
                            checked={f.enabled}
                            title={f.enabled ? 'Field is enabled' : 'Field is disabled'}
                            onChange={(e) => updateField(f.fieldKey, 'enabled', e.target.checked)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            className="cfg-input-order"
                            value={f.displayOrder}
                            onChange={(e) =>
                              updateField(f.fieldKey, 'displayOrder', parseInt(e.target.value, 10) || 1)
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Centralized Pagination */}
            <div className="cfg-pagination-wrap">
              <CommonPagination
                page={fieldPagination.page}
                pageSize={fieldPagination.pageSize}
                totalRecords={fieldPagination.totalRecords}
                onPageChange={fieldPagination.setPage}
                onPageSizeChange={fieldPagination.setPageSize}
                loading={loading}
              />
            </div>
          </>
        )}
      </div>

      {/* 6. Sticky Bottom Action Bar */}
      <div className={`cfg-sticky-bar ${hasChanges ? 'is-dirty' : ''}`}>
        <div className="cfg-sticky-info">
          {hasChanges ? (
            <span className="cfg-sticky-badge-dirty">● Unsaved Changes Pending</span>
          ) : (
            <span className="cfg-sticky-badge-saved">✓ All Settings Saved</span>
          )}
          <span className="cfg-sticky-centre-name">
            Target Centre: <strong>{centres.find((c) => c.centreId === selectedCentreId)?.centreName || `Centre #${selectedCentreId}`}</strong>
          </span>
        </div>
        <div className="cfg-sticky-actions">
          <button
            type="button"
            className="cfg-btn cfg-btn-secondary"
            onClick={handleReset}
            disabled={saving || loading}
          >
            Reset to Default
          </button>
          <button
            type="button"
            className="cfg-btn cfg-btn-preview"
            onClick={() => setPreviewOpen(true)}
          >
            Preview Form
          </button>
          <button
            type="button"
            className="cfg-btn cfg-btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Configuration *' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="cfg-modal-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="cfg-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <h3>Live Registration Form Preview (Centre: {selectedCentreId})</h3>
              <button
                type="button"
                className="cfg-modal-close"
                onClick={() => setPreviewOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="cfg-modal-body">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
                This live simulation illustrates how the patient registration form renders for this configuration.
              </p>

              {sections.map((sec) => {
                const secFields = fieldConfigs
                  .filter((f) => f.sectionKey === sec.sectionKey && f.visible)
                  .sort((a, b) => a.displayOrder - b.displayOrder);

                if (secFields.length === 0) return null;

                return (
                  <div key={sec.sectionKey} style={{ marginBottom: 20 }}>
                    <div className="cfg-preview-section-title">
                      {sec.effectiveLabel || sec.sectionName}
                    </div>
                    <div className="cfg-preview-grid">
                      {secFields.map((f) => (
                        <div
                          key={f.fieldKey}
                          className="cfg-preview-field"
                          style={{ gridColumn: `span ${f.columnSpan || 6}` }}
                        >
                          <label className="cfg-preview-label">
                            {f.displayLabel || f.fieldName}
                            {f.required && <span className="cfg-preview-req">*</span>}
                          </label>
                          <input
                            type={f.dataType === 'date' ? 'date' : 'text'}
                            className="cfg-preview-input"
                            placeholder={f.placeholder || f.displayLabel || f.fieldName}
                            disabled={f.readOnly}
                            readOnly={f.readOnly}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationConfigPage;
